/**
 * Pure calculations behind the EIA Gas Storage tool: storage seasons, the five year average and
 * range, and the aligned weekly table. Kept free of API and component imports so it runs in Jest.
 *
 * A storage season runs from November, when withdrawals start, to the following October. The
 * season is named after the year it starts in, so "2025-2026" is November 2025 to October 2026.
 * The chart follows seasons; the table runs through the calendar year, January to December, as
 * the old WebTools page did.
 */

export type StorageRegion = 'Total' | 'East' | 'Midwest' | 'Mountain' | 'Pacific' | 'SouthCentral' | 'SouthCentralSalt' | 'SouthCentralNonSalt';

export const STORAGE_REGIONS: StorageRegion[] = ['Total', 'East', 'Midwest', 'Mountain', 'Pacific', 'SouthCentral', 'SouthCentralSalt', 'SouthCentralNonSalt'];

export const STORAGE_REGION_LABELS: Record<StorageRegion, string> = {
  Total: 'Total Lower 48',
  East: 'East',
  Midwest: 'Midwest',
  Mountain: 'Mountain',
  Pacific: 'Pacific',
  SouthCentral: 'South Central',
  SouthCentralSalt: 'South Central Salt',
  SouthCentralNonSalt: 'South Central Nonsalt',
};

/** The fields of the API's week model that this logic reads. */
export interface StorageWeek {
  weekEnding: string;
  east?: number | null;
  midwest?: number | null;
  mountain?: number | null;
  pacific?: number | null;
  southCentral?: number | null;
  southCentralSalt?: number | null;
  southCentralNonSalt?: number | null;
  total: number;
}

export interface SeasonPoint {
  seasonStartYear: number;
  weekEnding: Date;
  /** Days since 1 November of the season start year. */
  dayOfSeason: number;
  /** Stocks in billion cubic feet. */
  volume: number;
  /** Change from the week before, or null for the first week on record. */
  netChange: number | null;
}

export interface FiveYearWeek {
  /** Average of the five prior years' stocks on the same calendar date, interpolated between their weekly readings. */
  average: number;
  minimum: number;
  maximum: number;
  /** Change in the average from a week earlier, or null when a prior year has no reading a week earlier. */
  netChange: number | null;
  /** How many of the five prior years had readings around the date. */
  yearCount: number;
}

export interface FiveYearStatistics extends FiveYearWeek {
  dayOfSeason: number;
}

/** Stocks an earlier year held on the same calendar date, interpolated between its weekly readings. */
export interface InterpolatedWeek {
  volume: number;
  /** Change from the interpolated stocks a week earlier, or null when the history does not reach that far. */
  netChange: number | null;
}

export interface SeasonSeries {
  seasonStartYear: number;
  label: string;
  points: SeasonPoint[];
}

export interface StorageTableRow {
  /** Position of the week in the calendar year, counting from the first week ending in January. */
  weekNumber: number;
  /** Week ending in the table's year, or the Friday the week will end on when it has not been reported yet. */
  weekEnding: Date;
  /** The year's reading for the week, when it has been reported. */
  current?: { volume: number; netChange: number | null };
  /** The year before, interpolated to the same calendar date. */
  yearAgo?: InterpolatedWeek;
  fiveYear?: FiveYearWeek;
  /** Stocks of the other selected years in their week nearest the same calendar date, by year. */
  otherYears: Record<number, number | undefined>;
}

/** The weekly table for one calendar year, January to December. */
export interface StorageTable {
  year: number;
  /** The other selected seasons' years, most recent first, shown beside the year before. */
  otherYears: number[];
  rows: StorageTableRow[];
}

export const SEASON_START_MONTH = 10; // November, zero based.
export const FIVE_YEAR_COUNT = 5;

const DAY_MILLISECONDS = 24 * 60 * 60 * 1000;
const WEEK_MATCH_TOLERANCE_DAYS = 3;
const REGION_FIELDS: Record<StorageRegion, keyof StorageWeek> = {
  Total: 'total',
  East: 'east',
  Midwest: 'midwest',
  Mountain: 'mountain',
  Pacific: 'pacific',
  SouthCentral: 'southCentral',
  SouthCentralSalt: 'southCentralSalt',
  SouthCentralNonSalt: 'southCentralNonSalt',
};

/** Reads the API's date (yyyy-MM-dd with or without a time) as a local calendar date. */
export function parseWeekEnding(weekEnding: string): Date {
  const [year, month, day] = weekEnding.substring(0, 10).split('-').map(Number);
  return new Date(year, month - 1, day);
}

export function regionVolume(week: StorageWeek, region: StorageRegion): number | null {
  const value = week[REGION_FIELDS[region]];
  return typeof value === 'number' ? value : null;
}

export function seasonStartYear(date: Date): number {
  return date.getMonth() >= SEASON_START_MONTH ? date.getFullYear() : date.getFullYear() - 1;
}

export function seasonLabel(startYear: number): string {
  return `${startYear}-${startYear + 1}`;
}

export function seasonStart(startYear: number): Date {
  return new Date(startYear, SEASON_START_MONTH, 1);
}

function daysBetween(from: Date, to: Date): number {
  return Math.round((Date.UTC(to.getFullYear(), to.getMonth(), to.getDate()) - Date.UTC(from.getFullYear(), from.getMonth(), from.getDate())) / DAY_MILLISECONDS);
}

export function dayOfSeason(date: Date): number {
  return daysBetween(seasonStart(seasonStartYear(date)), date);
}

export function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

/** The same calendar date in another year; 29 February rolls to 1 March. */
export function shiftYears(date: Date, years: number): Date {
  return new Date(date.getFullYear() + years, date.getMonth(), date.getDate());
}

/** Seasons with at least one week of data for the region, most recent first. */
export function availableSeasons(weeks: StorageWeek[], region: StorageRegion): number[] {
  const seasons = new Set<number>();
  for (const week of weeks) {
    if (regionVolume(week, region) !== null) {
      seasons.add(seasonStartYear(parseWeekEnding(week.weekEnding)));
    }
  }
  return Array.from(seasons).sort((a, b) => b - a);
}

/** Every week on record for the region in date order, with the change from the week before. */
export function regionPoints(weeks: StorageWeek[], region: StorageRegion): SeasonPoint[] {
  const sorted = weeks
    .map(week => ({ weekEnding: parseWeekEnding(week.weekEnding), volume: regionVolume(week, region) }))
    .filter((week): week is { weekEnding: Date; volume: number } => week.volume !== null)
    .sort((a, b) => a.weekEnding.getTime() - b.weekEnding.getTime());

  return sorted.map((week, index) => {
    const previous = index > 0 ? sorted[index - 1] : undefined;
    // Only a week that directly follows another counts as a weekly change.
    const netChange = previous && daysBetween(previous.weekEnding, week.weekEnding) <= 7 + WEEK_MATCH_TOLERANCE_DAYS ? week.volume - previous.volume : null;
    return {
      seasonStartYear: seasonStartYear(week.weekEnding),
      weekEnding: week.weekEnding,
      dayOfSeason: dayOfSeason(week.weekEnding),
      volume: week.volume,
      netChange,
    };
  });
}

export function seasonSeries(points: SeasonPoint[], startYears: number[]): SeasonSeries[] {
  return startYears.map(startYear => ({
    seasonStartYear: startYear,
    label: seasonLabel(startYear),
    points: points.filter(point => point.seasonStartYear === startYear),
  }));
}

/** Points by the time of their week ending, for quick nearest week lookups. */
export type PointIndex = Map<number, SeasonPoint>;

export function indexPoints(points: SeasonPoint[]): PointIndex {
  return new Map(points.map(point => [point.weekEnding.getTime(), point]));
}

/**
 * The week on record closest to the given date, if one lies within a few days. The EIA week ends
 * on a Friday, so the same week of a season lands a day or two later each year; matching by date
 * rather than by season keeps the weeks around 1 November aligned as well.
 */
export function findWeekNear(index: PointIndex, date: Date): SeasonPoint | undefined {
  for (let distance = 0; distance <= WEEK_MATCH_TOLERANCE_DAYS; distance++) {
    for (const offset of distance === 0 ? [0] : [distance, -distance]) {
      const match = index.get(addDays(date, offset).getTime());
      if (match) return match;
    }
  }
  return undefined;
}

/**
 * The days of season that make up a season's weekly template. A season that is still under way
 * is completed with the weekly cadence of its latest report so the five year lines run to October.
 */
export function seasonTemplateDays(points: SeasonPoint[], startYear: number): number[] {
  const seasonPoints = points.filter(point => point.seasonStartYear === startYear).sort((a, b) => a.dayOfSeason - b.dayOfSeason);
  const seasonLength = daysBetween(seasonStart(startYear), seasonStart(startYear + 1));

  if (seasonPoints.length === 0) {
    // No data yet: fall back to Fridays, which is when the EIA week ends.
    const firstFriday = (5 - seasonStart(startYear).getDay() + 7) % 7;
    const days: number[] = [];
    for (let day = firstFriday; day < seasonLength; day += 7) days.push(day);
    return days;
  }

  const days = seasonPoints.map(point => point.dayOfSeason);
  for (let day = days[days.length - 1] + 7; day < seasonLength; day += 7) days.push(day);
  for (let day = days[0] - 7; day >= 0; day -= 7) days.unshift(day);
  return days;
}

const INTERPOLATION_GAP_LIMIT_DAYS = 14;

/**
 * Stocks on a date, read straight off the weekly history when the date is a week ending and
 * otherwise interpolated in a straight line between the readings either side of it, as the old
 * WebTools page did. The EIA week ends on a Friday, so the same calendar date of an earlier year
 * falls a day or two away from a reading; interpolating takes that drift out of the comparison.
 * Undefined outside the history or where the readings either side are more than two weeks apart.
 */
export function interpolateVolume(points: SeasonPoint[], date: Date): number | undefined {
  const time = date.getTime();
  let low = 0;
  let high = points.length;
  while (low < high) {
    const middle = (low + high) >> 1;
    if (points[middle].weekEnding.getTime() < time) low = middle + 1;
    else high = middle;
  }

  const next = points[low];
  if (next && next.weekEnding.getTime() === time) return next.volume;
  if (!next || low === 0) return undefined;

  const previous = points[low - 1];
  const span = daysBetween(previous.weekEnding, next.weekEnding);
  if (span > INTERPOLATION_GAP_LIMIT_DAYS) return undefined;

  return previous.volume + ((next.volume - previous.volume) * daysBetween(previous.weekEnding, date)) / span;
}

function roundedInterpolation(points: SeasonPoint[], date: Date): number | undefined {
  const volume = interpolateVolume(points, date);
  return volume === undefined ? undefined : Math.round(volume);
}

/**
 * The stocks held some years earlier on the same calendar date as a week ending, with the change
 * from the interpolated stocks a week before that. Points must be in date order.
 */
export function priorYearWeek(points: SeasonPoint[], weekEnding: Date, yearsBack: number): InterpolatedWeek | undefined {
  const volume = roundedInterpolation(points, shiftYears(weekEnding, -yearsBack));
  if (volume === undefined) return undefined;
  const previous = roundedInterpolation(points, shiftYears(addDays(weekEnding, -7), -yearsBack));
  return { volume, netChange: previous === undefined ? null : volume - previous };
}

function average(values: number[]): number {
  return Math.round(values.reduce((sum, value) => sum + value, 0) / values.length);
}

/**
 * Average, minimum and maximum of the stocks the five years before held on a date, interpolated
 * between those years' weekly readings, or undefined when none of them has history around it.
 * Points must be in date order.
 */
export function fiveYearWeek(points: SeasonPoint[], date: Date): FiveYearWeek | undefined {
  const volumes: number[] = [];
  const previousVolumes: number[] = [];
  for (let yearsBack = 1; yearsBack <= FIVE_YEAR_COUNT; yearsBack++) {
    const volume = roundedInterpolation(points, shiftYears(date, -yearsBack));
    if (volume === undefined) continue;
    volumes.push(volume);
    const previous = roundedInterpolation(points, shiftYears(addDays(date, -7), -yearsBack));
    if (previous !== undefined) previousVolumes.push(previous);
  }

  if (volumes.length === 0) return undefined;

  const mean = average(volumes);
  return {
    average: mean,
    minimum: Math.min(...volumes),
    maximum: Math.max(...volumes),
    netChange: previousVolumes.length === volumes.length ? mean - average(previousVolumes) : null,
    yearCount: volumes.length,
  };
}

/**
 * The five year statistics for every week of a season's template, for the chart. Weeks with no
 * history at all are left out. Points must be in date order.
 */
export function fiveYearStatistics(points: SeasonPoint[], startYear: number): FiveYearStatistics[] {
  const statistics: FiveYearStatistics[] = [];
  for (const day of seasonTemplateDays(points, startYear)) {
    const week = fiveYearWeek(points, addDays(seasonStart(startYear), day));
    if (week) statistics.push({ dayOfSeason: day, ...week });
  }
  return statistics;
}

/**
 * The calendar year a season's table runs through: the year of its latest week, which is the year
 * the season ends in once it has reached January.
 */
export function seasonTableYear(points: SeasonPoint[], startYear: number): number {
  let latest: SeasonPoint | undefined;
  for (const point of points) {
    if (point.seasonStartYear === startYear) latest = point;
  }
  return latest ? latest.weekEnding.getFullYear() : startYear + 1;
}

/** Every Friday of a calendar year, the day the EIA week ends, from the first in January to the last in December. */
export function yearWeekEndings(year: number): Date[] {
  const newYear = new Date(year, 0, 1);
  const dates: Date[] = [];
  for (let date = addDays(newYear, (5 - newYear.getDay() + 7) % 7); date.getFullYear() === year; date = addDays(date, 7)) {
    dates.push(date);
  }
  return dates;
}

/**
 * The weekly table for the year of the most recent selected season: each week's reading beside
 * the year before interpolated to the same date, the five year statistics for the date and the
 * other selected seasons' readings for the same week of their year. Points must be in date order.
 */
export function buildStorageTable(points: SeasonPoint[], startYears: number[]): StorageTable | undefined {
  if (startYears.length === 0) return undefined;

  const year = seasonTableYear(points, Math.max(...startYears));
  const otherYears = Array.from(new Set(startYears.map(startYear => seasonTableYear(points, startYear))))
    .filter(otherYear => otherYear !== year && otherYear !== year - 1)
    .sort((a, b) => b - a);
  const index = indexPoints(points);

  const rows = yearWeekEndings(year).map((friday, position) => {
    const current = findWeekNear(index, friday);
    const weekEnding = current?.weekEnding ?? friday;
    const otherVolumes: StorageTableRow['otherYears'] = {};
    for (const otherYear of otherYears) {
      otherVolumes[otherYear] = findWeekNear(index, shiftYears(weekEnding, otherYear - year))?.volume;
    }
    return {
      weekNumber: position + 1,
      weekEnding,
      current: current ? { volume: current.volume, netChange: current.netChange } : undefined,
      yearAgo: priorYearWeek(points, weekEnding, 1),
      fiveYear: fiveYearWeek(points, weekEnding),
      otherYears: otherVolumes,
    };
  });

  return { year, otherYears, rows };
}

/** Formats a day of season on the chart's reference axis, which places every season in the same year. */
export const CHART_REFERENCE_SEASON_START_YEAR = 2001;

export function chartAxisValue(day: number): number {
  return Date.UTC(CHART_REFERENCE_SEASON_START_YEAR, SEASON_START_MONTH, 1 + day);
}
